<template>
  <v-dialog
    :value="value"
    persistent
    max-width="820px"
    @click:outside="closeModal">
    <v-card>
      <v-card-title>
        <v-icon color="primary">mdi-store</v-icon>
        <span class="pl-2">{{ storeParty.store_name }}</span>
        <span v-if="stores.length" class="pl-2 text-body-2 grey--text">({{ stores.length }} {{ stores.length === 1 ? 'store' : 'stores' }})</span>
        <v-spacer/>
        <v-btn v-if="customerPortalBase && canManageStoreGroups" text small color="primary" class="mr-2" @click="openInCustomerUi">
          <v-icon left small>mdi-launch</v-icon>Manage in Customer UI
        </v-btn>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="sortedStores"
        :height="250"
        :items-per-page="-1"
        dense
        hide-default-footer>
        <template #item="{ item }">
          <tr :style="isPrimary(item) ? primaryRowStyle : null">
            <td class="noWrap">
              {{ item.store_name }}
              <v-chip v-if="isPrimary(item)" x-small color="primary" label class="ml-2">Primary</v-chip>
            </td>
            <td class="text-center">
              <v-chip v-if="isPricePro(item)" x-small color="save" text-color="white" label>Price Pro</v-chip>
              <span v-else class="grey--text">&mdash;</span>
            </td>
            <td class="noWrap">{{ formatDateDisplay(item.start_dt) }}</td>
            <td class="noWrap">{{ formatDateDisplay(item.end_dt) }}</td>
            <td class="noWrap">{{ formatDateTimestampDisplay(item.created_on) }}</td>
            <td v-if="canViewStoreId" class="text-center">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click="copyStoreId(item)">
                    <v-icon small :color="copiedId === storeId(item) ? 'save' : 'primary'">
                      {{ copiedId === storeId(item) ? 'mdi-check' : 'mdi-content-copy' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ copiedId === storeId(item) ? 'Copied!' : 'Copy store ID' }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <label class="font-weight-light mx-4 note-label">Note: The primary store of a store group is determined by the store relationship's earliest start date. A tie is broken by the relationship's created date.</label>
    </v-card>
  </v-dialog>
</template>
<script>
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'

export default {
  name: 'StoreGroupModal',
  mixins: [displayHelpers, userAccess],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    storeParty: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      copiedId: null
    }
  },
  computed: {
    // Raw party UUID: gate the column to admins/support.
    canViewStoreId () {
      return this.adminUser || ['arg-admin', 'arg-support'].some(role => this.userGroups.includes(role))
    },
    headers () {
      const cols = [
        { text: 'Store Name', value: 'store_name', class: 'accent white--text', align: 'start' },
        { text: 'Price Pro', value: 'price_pro', class: 'accent white--text', align: 'center', sortable: false },
        { text: 'Start Date', value: 'start_dt', class: 'accent white--text' },
        { text: 'End Date', value: 'end_dt', class: 'accent white--text' },
        { text: 'Created On', value: 'created_on', class: 'accent white--text' }
      ]
      if (this.canViewStoreId) {
        cols.push({ text: 'Store ID', value: 'copy', class: 'accent white--text', align: 'center', sortable: false })
      }
      return cols
    },
    stores () {
      return this.storeParty.children || []
    },
    // Primary first: earliest start date, tie broken by created date (matches the note).
    sortedStores () {
      return [...this.stores].sort(this.byPrimaryOrder)
    },
    primaryStoreId () {
      return this.sortedStores.length ? this.storeId(this.sortedStores[0]) : null
    },
    // theme primary + '1F' = ~12% alpha tint.
    primaryRowStyle () {
      return { backgroundColor: `${this.$vuetify.theme.currentTheme.primary}1F` }
    },
    customerPortalBase () {
      return process.env.VUE_APP_CUSTOMER_PORTAL
    },
    // Show only to users with customer party write access (matches customer-ui).
    canManageStoreGroups () {
      const perms = (this.$auth && this.$auth.permissions) || []
      return ['party:create', 'party:read', 'party:update'].every(scope => perms.includes(scope))
    }
  },
  methods: {
    storeId (store) {
      return store.store_party_id || store.party_id
    },
    isPrimary (store) {
      return this.storeId(store) === this.primaryStoreId
    },
    isPricePro (store) {
      const flag = store.is_price_pro_store
      return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
    },
    byPrimaryOrder (a, b) {
      // missing/invalid dates sort last so they never become the primary
      const ts = value => {
        const t = new Date(value).getTime()
        return isNaN(t) ? Infinity : t
      }
      const start = ts(a.start_dt) - ts(b.start_dt)
      return start !== 0 ? start : ts(a.created_on) - ts(b.created_on)
    },
    async copyStoreId (store) {
      const id = this.storeId(store)
      if (!id) return
      try {
        await navigator.clipboard.writeText(id)
      } catch (e) {
        const el = document.createElement('textarea')
        el.value = id
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.copiedId = id
      setTimeout(() => { if (this.copiedId === id) this.copiedId = null }, 1500)
    },
    openInCustomerUi () {
      const base = this.customerPortalBase
      if (!base) return
      const prefix = this.$auth && this.$auth.tenantURLPrefix
      const root = prefix ? `https://${prefix}.${base}` : `https://${base}`
      const id = this.storeId(this.storeParty)
      const url = id ? `${root}/parties_edit?id=${id}` : `${root}/parties`
      window.open(url, '_blank', 'noopener')
    },
    closeModal() {
      this.$emit('update:value', false)
    }
  }
}
</script>
<style scoped>
.note-label {
  display: block;
}
</style>
